import React, { useEffect, useState } from 'react';
import { teacherApi, adminApi } from '../../api/client';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import { FaPlus, FaEnvelope, FaEnvelopeOpen, FaReply } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

export default function TeacherMessages() {
  const { user } = useAuth();
  const [tab,      setTab]      = useState('inbox');
  const [messages, setMessages] = useState([]);
  const [parents,  setParents]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(false);
  const [selected, setSelected] = useState(null);
  const [reply,    setReply]    = useState(false);
  const [form,     setForm]     = useState({ receiver_id: '', subject: '', body: '' });

  const load = () => {
    setLoading(true);
    teacherApi.getMessages(tab)
      .then(r => setMessages(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  // Load learners to get parent contacts (teachers message school users/parents)
  useEffect(() => {
    adminApi.getLearners({})
      .then(r => setParents(r.data?.data || []))
      .catch(() => {});
  }, []);

  const openMessage = async (msg) => {
    setSelected(msg);
    setReply(false);
    if (!msg.is_read && tab === 'inbox') {
      await teacherApi.markRead(msg.message_id).catch(() => {});
      setMessages(ms => ms.map(m => m.message_id === msg.message_id ? { ...m, is_read: 1 } : m));
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    try {
      await teacherApi.sendMessage(form);
      toast.success('Message sent');
      setModal(false);
      setReply(false);
      setForm({ receiver_id: '', subject: '', body: '' });
      if (tab === 'sent') load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to send'); }
  };

  const handleReply = () => {
    setForm({
      receiver_id: selected.sender_id || '',
      subject: `Re: ${selected.subject || '(no subject)'}`,
      body: '',
    });
    setReply(true);
  };

  const unread = messages.filter(m => !m.is_read && tab === 'inbox').length;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>Messages</h2>
          <p>Communicate with parents and staff</p>
        </div>
        <button className="sms-btn sms-btn-primary" onClick={() => { setForm({ receiver_id:'', subject:'', body:'' }); setModal(true); }}>
          <FaPlus size={13} /> New Message
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f3f4f6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[['inbox','Inbox'], ['sent','Sent']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className="sms-btn"
            style={{ background: tab===k ? '#fff' : 'transparent', boxShadow: tab===k ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', color: tab===k ? '#111' : '#6b7280', padding: '7px 18px', borderRadius: 8, position: 'relative' }}>
            {l}
            {k === 'inbox' && unread > 0 && (
              <span style={{ position: 'absolute', top: 2, right: 2, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 16, height: 16, fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                {unread}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 20 }}>
        {/* Message list */}
        <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? <Spinner /> : messages.length === 0
            ? <div style={{ textAlign: 'center', color: '#9ca3af', padding: '60px 20px' }}>
                <FaEnvelope size={36} style={{ marginBottom: 10, opacity: 0.3 }} />
                <p>No messages</p>
              </div>
            : messages.map(m => (
              <div key={m.message_id}
                onClick={() => openMessage(m)}
                style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: selected?.message_id === m.message_id ? '#eff6ff' : !m.is_read && tab === 'inbox' ? '#fafbff' : '#fff', transition: 'background 0.15s' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {!m.is_read && tab === 'inbox'
                      ? <FaEnvelope size={13} color="#0d6efd" />
                      : <FaEnvelopeOpen size={13} color="#d1d5db" />
                    }
                    <span style={{ fontWeight: !m.is_read && tab === 'inbox' ? 700 : 500, fontSize: '0.88rem', color: '#374151' }}>
                      {tab === 'inbox' ? m.sender_name : m.receiver_name}
                    </span>
                    {m.sender_role && (
                      <span style={{ fontSize: '0.68rem', background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: 8, textTransform: 'capitalize' }}>
                        {m.sender_role}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '0.72rem', color: '#9ca3af', flexShrink: 0 }}>
                    {new Date(m.sent_at).toLocaleDateString()}
                  </span>
                </div>
                {m.subject && <div style={{ fontWeight: 600, fontSize: '0.83rem', color: '#111827', marginBottom: 2 }}>{m.subject}</div>}
                <div style={{ color: '#9ca3af', fontSize: '0.79rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.body}
                </div>
              </div>
            ))
          }
        </div>

        {/* Message detail */}
        <div className="sms-card">
          {selected && !reply ? (
            <div>
              <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
                <h5 style={{ fontWeight: 600, marginBottom: 8 }}>{selected.subject || '(No subject)'}</h5>
                <div style={{ display: 'flex', gap: 16, fontSize: '0.8rem', color: '#6b7280', flexWrap: 'wrap' }}>
                  <span><strong>{tab === 'inbox' ? 'From' : 'To'}:</strong> {tab === 'inbox' ? selected.sender_name : selected.receiver_name}</span>
                  <span>{new Date(selected.sent_at).toLocaleString()}</span>
                </div>
              </div>
              <p style={{ color: '#374151', lineHeight: 1.8, whiteSpace: 'pre-wrap', marginBottom: 20 }}>{selected.body}</p>
              {tab === 'inbox' && (
                <button className="sms-btn sms-btn-outline" onClick={handleReply}>
                  <FaReply size={12} /> Reply
                </button>
              )}
            </div>
          ) : selected && reply ? (
            <form onSubmit={handleSend}>
              <div style={{ fontWeight: 600, marginBottom: 14, color: '#374151' }}>
                Reply to {selected.sender_name}
              </div>
              <div style={{ marginBottom: 14 }}>
                <label className="sms-label">Subject</label>
                <input className="sms-input" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label className="sms-label">Message <span style={{ color: '#ef4444' }}>*</span></label>
                <textarea className="sms-input" rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} required style={{ resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="sms-btn sms-btn-primary">Send Reply</button>
                <button type="button" className="sms-btn sms-btn-outline" onClick={() => setReply(false)}>Cancel</button>
              </div>
            </form>
          ) : (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '60px 0' }}>
              <FaEnvelopeOpen size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
              <p>Select a message to read it</p>
            </div>
          )}
        </div>
      </div>

      {/* New message modal */}
      <Modal show={modal} onClose={() => setModal(false)} title="New Message">
        <form onSubmit={handleSend}>
          <div style={{ marginBottom: 14 }}>
            <label className="sms-label">To <span style={{ color: '#ef4444' }}>*</span></label>
            <select className="sms-input" value={form.receiver_id} onChange={e => setForm({ ...form, receiver_id: e.target.value })} required>
              <option value="">Select recipient</option>
              <optgroup label="Learners / Parents">
                {parents.map(l => (
                  <option key={l.user_id} value={l.user_id}>{l.first_name} {l.surname} (Gr {l.grade_number || '?'})</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="sms-label">Subject</label>
            <input className="sms-input" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Optional subject" />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label className="sms-label">Message <span style={{ color: '#ef4444' }}>*</span></label>
            <textarea className="sms-input" rows={5} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} required style={{ resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="sms-btn sms-btn-primary">Send</button>
            <button type="button" className="sms-btn sms-btn-outline" onClick={() => setModal(false)}>Cancel</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
